/**
 * Where the person's deliveries to Console Next stand, shared by the Publish dialog, the project Home and the
 * notifications bell: one live poll (utils/livePoll.js) of Console Next's `GET /api/deliveries/status` for every Flow
 * project this browser delivered or is showing, every 20 s while someone looks (a hidden tab asks nothing). Only when
 * the app's config names a Console Next (`consoleNextUrl`, hub-fe-config.json); and once Console Next cannot be reached
 * at all (nothing listens at that URL) the poll stops for this page, until a delivery is sent from it.
 *
 * When a delivery's state changes, the bell gets a notice ("Your app was deployed to dev"). The notices and the last
 * state seen per project are kept in this browser (localStorage, best effort): Console Next has no way to reach
 * Flow's inbox as the person, and the delivery itself goes from the browser too.
 *
 * Each deploy Console Next finished (a stage's `lastDeploy`: succeeded or failed) goes into the project's History
 * (vfs-service keeps one event per deploy id, however many browsers report it; this browser asks once per id).
 */
import { useEffect } from 'react';
import { consoleNextEnabled, consoleWebUrl, getDeliveryStatuses } from '../services/ConsoleNextService';
import { recordProvenanceEvent } from '../utils/vfsClient';
import { createLivePoll, useLivePoll } from '../utils/livePoll';

export const CONSOLE_POLL_MS = 20000;
export const TRACKED_KEY = 'flow.consoleDeliveries.v1';
export const NOTICES_KEY = 'flow.consoleNotices.v1';
export const REPORTED_KEY = 'flow.consoleDeploysReported.v1';
const MAX_REPORTED = 200;
const MAX_TRACKED = 20;
const MAX_NOTICES = 20;

function readJson(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    const value = raw ? JSON.parse(raw) : null;
    return value && typeof value === 'object' ? value : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* private window, full storage */ }
}

const keyOf = (item) => `${item.version || ''}:${item.state}`;

/** The bell's line for a delivery that reached `state`, or null when that state is not worth a notice. */
export function noticeText(state) {
  switch (state) {
    case 'review': return 'Your app is waiting for review in Console';
    case 'ready': return 'Your app is built and ready to deploy';
    case 'deployed': return 'Your app was deployed to dev';
    case 'failed': return 'Your app could not be delivered';
    default: return null;
  }
}

/**
 * Compares the answer with the last state seen per project. Returns the new `tracked` map and the notices for the
 * changes: a project seen for the first time is only remembered, and "waiting for review" right after the person sent
 * it (the Publish dialog says so) is not a notice.
 */
export function deliveryNotices(tracked, items, now = new Date().toISOString()) {
  const next = { ...(tracked || {}) };
  const notices = [];
  for (const item of items || []) {
    if (!item?.flowProject || !item.state) { continue; }
    const prev = next[item.flowProject];
    const key = keyOf(item);
    const text = noticeText(item.state);
    const justSent = prev?.state === 'received' && item.state === 'review';
    if (prev && prev.key !== key && text && !justSent) {
      notices.push({
        id: `${item.flowProject}:${key}`,
        projectId: item.flowProject,
        system: item.system,
        title: item.title || item.system,
        version: item.version,
        state: item.state,
        text,
        detail: item.state === 'failed' ? (item.reason || item.detail || null) : null,
        link: item.state === 'deployed' && item.devUrl ? item.devUrl : consoleWebUrl(item.openPath || item.projectPath),
        linkLabel: item.state === 'deployed' && item.devUrl ? 'Open app' : item.state === 'review' ? 'Review in Console' : 'Open in Console',
        at: now,
        read: false,
      });
    }
    next[item.flowProject] = { key, state: item.state, version: item.version, system: item.system, at: prev?.key === key ? prev.at : now };
  }
  const kept = Object.entries(next).sort((a, b) => String(b[1].at || '').localeCompare(String(a[1].at || ''))).slice(0, MAX_TRACKED);
  return { tracked: Object.fromEntries(kept), notices };
}

/**
 * The History events of the finished deploys in `items` this browser has not reported yet (`reported`: keys
 * `<project>:<deploy id>`): `[{ key, projectId, body }]`, `body` as vfs-service's `POST /provenance/events` takes it.
 */
export function finishedDeploys(items, reported = []) {
  const done = new Set(reported);
  const out = [];
  for (const item of items || []) {
    for (const stage of item?.flowProject ? item.stages || [] : []) {
      const last = stage?.lastDeploy;
      const key = last?.id ? `${item.flowProject}:${last.id}` : null;
      if (!key || done.has(key)) { continue; }
      done.add(key);
      const deploy = {
        kind: 'console-next',
        system: item.system,
        version: last.version || undefined,
        environment: stage.stage || stage.environment,
        deployId: last.id,
        outcome: last.outcome,
        reason: last.reason || undefined,
        finishedAt: last.finishedAt || undefined,
        // The app's address there now, when this deploy is what runs; the deploy's page in Console Next.
        url: last.outcome === 'success' && stage.url && stage.version === last.version ? stage.url : undefined,
        link: last.path ? consoleWebUrl(last.path) : undefined,
      };
      out.push({ key, projectId: item.flowProject, body: { action: 'deploy', target: { deploy } } });
    }
  }
  return out;
}

function reportDeploys(items, notices) {
  const reported = readJson(REPORTED_KEY, []);
  const fresh = finishedDeploys(items, Array.isArray(reported) ? reported : []);
  fresh.forEach(({ projectId, body }) => { recordProvenanceEvent(projectId, body).catch(() => {}); });
  if (fresh.length) {
    writeJson(REPORTED_KEY, [...(Array.isArray(reported) ? reported : []), ...fresh.map((f) => f.key)].slice(-MAX_REPORTED));
  }
  // A Console Next that does not name its deploys yet: the deploy to dev this browser saw happen, as before.
  const named = new Set((items || []).filter((i) => (i.stages || []).some((st) => st?.lastDeploy)).map((i) => i.flowProject));
  notices
    .filter((n) => n.state === 'deployed' && n.projectId && !named.has(n.projectId))
    .forEach((n) => {
      recordProvenanceEvent(n.projectId, { action: 'deploy', target: { deploy: { kind: 'console-next', system: n.system, version: n.version, environment: 'dev' } } }).catch(() => {});
    });
}

// Projects shown right now (Home, Publish dialog), counted per caller.
const watched = new Map();

function idsToAsk() {
  return [...new Set([...watched.keys(), ...Object.keys(readJson(TRACKED_KEY, {}))])];
}

function readNotices() {
  const list = readJson(NOTICES_KEY, []);
  return Array.isArray(list) ? list : [];
}

async function load() {
  const ids = idsToAsk();
  let items = [];
  if (consoleNextEnabled() && ids.length > 0) {
    try {
      items = await getDeliveryStatuses(ids);
    } catch (error) {
      // Not reachable: an answer that halts the poll (haltWhen below); any other failure is a blip, asked again later.
      if (error?.unreachable) { return { items: {}, notices: readNotices(), unreachable: true }; }
      throw error;
    }
    const { tracked, notices } = deliveryNotices(readJson(TRACKED_KEY, {}), items);
    writeJson(TRACKED_KEY, tracked);
    if (notices.length) {
      const fresh = new Set(notices.map((n) => n.id));
      writeJson(NOTICES_KEY, [...notices, ...readNotices().filter((n) => !fresh.has(n.id))].slice(0, MAX_NOTICES));
    }
    // The project's History says it too ("Console Next deployed version 0.1.3 to dev"); never fails the poll.
    try { reportDeploys(items, notices); } catch { /* the History is best effort */ }
  }
  return { items: Object.fromEntries(items.map((item) => [item.flowProject, item])), notices: readNotices() };
}

const poll = createLivePoll({ load, intervalMs: CONSOLE_POLL_MS, haltWhen: (answer) => Boolean(answer?.unreachable) });

export function refreshConsoleDeliveries() {
  return poll.refresh();
}

/** After "Send to Console": remember the project (so the bell follows it) and ask at once. */
export function trackConsoleDelivery(projectId, delivery) {
  if (!projectId) { return; }
  const tracked = readJson(TRACKED_KEY, {});
  const version = delivery?.version != null ? String(delivery.version) : '';
  tracked[projectId] = { key: `${version}:received`, state: 'received', version, system: delivery?.system, at: new Date().toISOString() };
  writeJson(TRACKED_KEY, tracked);
  poll.refresh().catch(() => {});
}

/** Marks `ids` (or every notice, `null`) read. */
export function readConsoleNotices(ids) {
  const all = !ids;
  const list = readNotices().map((n) => (all || ids.includes(n.id) ? { ...n, read: true } : n));
  writeJson(NOTICES_KEY, list);
  const state = poll.getSnapshot();
  if (state) { poll.set({ ...state, notices: list }); }
}

/** For tests: forget everything. */
export function resetConsoleDeliveries() {
  watched.clear();
  poll.reset();
}

/** `{ state, notices, unread }` for the bell: null `state` until the first answer. */
export function useConsoleNotices({ enabled = true } = {}) {
  const on = enabled && consoleNextEnabled();
  const state = useLivePoll(poll, { enabled: on });
  const notices = on ? (state?.notices || []) : [];
  return { notices, unread: notices.filter((n) => !n.read).length, read: readConsoleNotices };
}

/** The status of one project's delivery (`undefined` until the first answer, `null` when never delivered). */
export default function useConsoleDelivery(projectId) {
  const on = Boolean(projectId) && consoleNextEnabled();
  useEffect(() => {
    if (!on) { return undefined; }
    watched.set(projectId, (watched.get(projectId) || 0) + 1);
    // Console Next was not reachable: a project shown later does not ask again (a delivery sent does).
    if (!poll.halted()) { poll.refresh().catch(() => {}); }
    return () => {
      const n = (watched.get(projectId) || 1) - 1;
      if (n <= 0) { watched.delete(projectId); } else { watched.set(projectId, n); }
    };
  }, [on, projectId]);
  const state = useLivePoll(poll, { enabled: on });
  if (!on) { return null; }
  if (!state) { return undefined; }
  return state.items?.[projectId] || null;
}
